import { Box, Button, Typography } from '@mui/material';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';

export interface StepUpNoPhoneDialogProps {
  open: boolean;
  /** Supporting text; defaults to the bank-link wording (SEE-221). */
  body?: string;
  onGoToProfile: () => void;
  onClose: () => void;
}

const DEFAULT_BODY =
  'We don\u2019t have a mobile number on your profile, so we can\u2019t text you a verification code. Add a mobile number in your profile, then come back to link your bank account.';

// Design tokens — Material 3 basic dialog (matches StepUpIntroDialog).
const COLOR_TITLE = '#3A3B41';
const COLOR_SUPPORT = '#44474F';
const COLOR_PRIMARY = '#193059';
const COLOR_PRIMARY_HOVER = '#142747';
const COLOR_SURFACE = '#E8E7EF';

/**
 * SEE-221: shown when the caller has no mobile number on file, so we can't send
 * a step-up code. Points them to their profile to add one instead of a dead-end
 * "we couldn't send a code" error.
 */
function StepUpNoPhoneDialog({
  open,
  body = DEFAULT_BODY,
  onGoToProfile,
  onClose,
}: Readonly<StepUpNoPhoneDialogProps>): JSX.Element {
  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 360 },
          maxWidth: '100%',
          bgcolor: COLOR_SURFACE,
          borderRadius: '28px',
        },
      }}
    >
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Typography
          component="h2"
          sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 400, color: COLOR_TITLE }}
        >
          Add a mobile number
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.25px', color: COLOR_SUPPORT }}>
          {body}
        </Typography>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', mt: '8px' }}
        >
          <Button
            onClick={onClose}
            variant="text"
            sx={{
              color: COLOR_PRIMARY,
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: '20px',
              px: '12px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onGoToProfile}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: COLOR_PRIMARY,
              color: '#fff',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: '12px',
              px: '24px',
              '&:hover': { bgcolor: COLOR_PRIMARY_HOVER },
            }}
          >
            Go to profile
          </Button>
        </Box>
      </Box>
    </ResponsiveDialog>
  );
}

export default StepUpNoPhoneDialog;
